<template>
    <v-layout column style="overflow:hidden;" id="viewport" class="students3d-viewport">
        <fe-crud ref="prefsCrud" :config="prefsCrudConfig" @read="prefsFetched" autoload/>
        <div class="students3d-demographic-wrap">
            <demographic-search
                ref="demographicSearch"
                :value="params.search"
                @input="params.search = $event"
                @coordinated-search="handleCoordinatedSearch"
                @search-clicked="onSearchClicked"
            />
        </div>
        <div class="students3d-main-area">
            <div class="students3d-main-column">
                <div class="students3d-top-bar">
                    <div class="top-bar-filters-row d-flex flex-wrap align-center pt-2 pb-1">
                        <span class="filters-label mr-2 ml-2">Filters:</span>
                        <component
                            v-for="panel in orderedFilterPanels"
                            v-show="checkShow(panel)"
                            :key="panel.name"
                            :ref="panel.name"
                            :is="panel"
                            v-bind="panel.name === 'MoreFiltersPanel' ? { checkShow } : {}"
                            @input="setFilter(...arguments)"
                            @apply-more-filters="applyMoreFilters"
                            :params="params"
                            class="filter-panel-btn mr-2 mb-1"
                        />
                    </div>
                    <div
                        class="d-flex align-center students3d-group-tabs-row"
                        :style="groupTabsRowStyle"
                    >
                        <fe-tabs
                            ref="groupTabsRef"
                            class="students3d-group-tabs"
                            :tabs="groupTabsWithByPrefix"
                            :value="activeGroupTabIndex"
                            show-arrows
                            @tabClicked="onGroupTabClick"
                        />
                    </div>
                </div>
                <v-layout row fill-height class="students3d-pixi-row">
                    <div class="pixiwrapper">
                        <main-panel ref="mainPanelRef" />
                    </div>
                </v-layout>
                <button
                    v-if="aiFeatureEnabled"
                    type="button"
                    class="nlf-agent-panel-toggle"
                    :style="aiPanelToggleButtonStyle"
                    :aria-expanded="(!aiPanelCollapsed).toString()"
                    aria-controls="nlf-agent-side-panel"
                    :aria-label="aiPanelCollapsed ? 'Expand AI assistant panel' : 'Collapse AI assistant panel'"
                    data-test="nlf-agent-panel-toggle-btn"
                    @click="aiPanelCollapsed = !aiPanelCollapsed"
                >
                    <i
                        :class="aiPanelCollapsed ? 'fas fa-arrow-to-left' : 'fas fa-arrow-to-right'"
                        aria-hidden="true"
                    ></i>
                </button>
            </div>
            <right-side-panel
                class="students3d-right-panel-anchor"
                :show="aiFeatureEnabled && !aiPanelCollapsed"
                @send-message="handleSendMessage"
                @select-option="handleSelectOption"
                @reset-chat="handleResetChat"
            />
        </div>
    </v-layout>
</template>

<script>
import * as Util from './Util'
import Group from './Group'
import Vuex from 'vuex'
import { mapLocalState } from '@/util/vuexHelper'
import { mapState } from 'vuex'
import * as NlfAgentProcessor from './NlfAgentProcessor'
import DemographicSearch from './components/DemographicSearch'
import MainPanel from './components/MainPanel'
import RightSidePanel from './components/RightSidePanel'
import AssessmentPanel from './components/filterPanels/AssessmentPanel'
import TagPanel from './components/filterPanels/TagPanel'
import GenderPanel from './components/filterPanels/GenderPanel'
import AttendancePanel from './components/filterPanels/AttendancePanel'
import DemographicsPanel from './components/filterPanels/DemographicsPanel'
import IncidentPanel from './components/filterPanels/IncidentPanel'
import MoreFiltersPanel from './components/filterPanels/MoreFiltersPanel'
import { clearFilterCache } from './NlfAgentProcessor'

const SCHOOL_GROUP_GROUPER_NAME = 'School Group'

const GROUPER_TAB_ORDER = ['Disability', 'English Learner', 'Grade', 'Ethnicity', 'Gender', 'School', 'Meal Status', SCHOOL_GROUP_GROUPER_NAME]

const FILTER_PANEL_ORDER = [
    AssessmentPanel,
    TagPanel,
    GenderPanel,
    AttendancePanel,
    DemographicsPanel,
    IncidentPanel,
    MoreFiltersPanel,
]

export default {
    name: 'Students3D',

    components: {
        DemographicSearch,
        MainPanel,
        RightSidePanel,
    },

    data() {
        return {
            aiPanelCollapsed: false,
            // True only while assigning backfill to params.search so that mutation does not re-trigger the search watcher
            executeSearchInProgress: false,
        }
    },

    computed: {
        ...mapLocalState(['params', 'charts', 'activeGrouping', 'activeLayout', 'atlas', 'ss3', 'vueLoaderId', 'groupers', 'aiModeEnabled']),
        ...mapState('global', ['sessionUser']),
        aiFeatureEnabled() {
            return this.$store.state.manage.districtPreferences?.STUDENTS_THREED_AGENT_ENABLED === true
        },
        prefsCrudConfig() {
            let cfg = this.$_.cloneDeep(this.$models.preferences)
            cfg.read.params.property = 'district'
            return cfg
        },
        ellLevelEnabled() {
            return this.$store.state.global.shareableStores.ell_level_enabled
        },
        orderedFilterPanels() {
            return FILTER_PANEL_ORDER
        },
        extraGroupers() {
            const groupers = Array.isArray(this.groupers) ? this.groupers : []
            return groupers.filter(g => !GROUPER_TAB_ORDER.includes(g.name) && this.checkShow(g))
        },
        groupTabsForFeTabs() {
            return this.groupTabs
        },
        groupTabsWithByPrefix() {
            const tabs = this.groupTabsForFeTabs
            if (!tabs.length) return tabs
            return tabs.map((tab, i) => {
                if (i === 0) return { ...tab, title: tab.name }
                const name = 'By ' + tab.name
                const displayLabel = tab.displayLabel != null ? 'By ' + tab.displayLabel : undefined
                return {
                    ...tab,
                    name,
                    displayLabel,
                    title: displayLabel != null ? displayLabel : name,
                }
            })
        },
        groupTabs() {
            const groupers = Array.isArray(this.groupers) ? this.groupers : []
            const normalized = (s) => s.toLowerCase().replace(/\s+/g, '-')
            const dataTest = (name) => `students3d-group-by-${normalized(name)}-tab`
            const tabs = [
                { name: 'Overall', grouper: null, path: '#', dataTest: dataTest('Overall') },
            ]
            GROUPER_TAB_ORDER.forEach(name => {
                const grouper = groupers.find(g => g.name === name)
                if (!grouper || !this.checkShow(grouper)) return
                if (grouper.subGroupers) {
                    grouper.subGroupers.forEach(sub => {
                        tabs.push({ name: sub.name, grouper: sub, path: '#', dataTest: dataTest(sub.name) })
                    })
                } else {
                    tabs.push({ name, grouper, path: '#', dataTest: dataTest(name) })
                }
            })
            this.extraGroupers.forEach(extra => {
                tabs.push({ name: extra.name, grouper: extra, path: '#', dataTest: dataTest(extra.name) })
            })
            return tabs
        },
        activeGroupTabIndex() {
            const tabs = this.groupTabsForFeTabs
            if (!tabs.length) return 0
            const active = this.activeGrouping.grouper
            if (!active) return 0
            let idx = tabs.findIndex(t => t.grouper === active)
            if (idx >= 0) return idx
            if (active.subGroupers?.length) {
                idx = tabs.findIndex(t => t.grouper && active.subGroupers.some(sub => sub === t.grouper))
                if (idx >= 0) return idx
                idx = tabs.findIndex(t => t.grouper && active.subGroupers.some(sub => sub.id === t.grouper.id && sub.name === t.grouper.name))
            }
            if (idx >= 0) return idx
            idx = tabs.findIndex(t => t.grouper && t.grouper.id === active.id && t.grouper.name === active.name)
            return idx >= 0 ? idx : 0
        },
        groupTabsRowStyle() {
            const count = this.groupTabsForFeTabs.length
            return { '--students3d-tab-bar-min-width': `${count * 90}px` }
        },
        aiPanelToggleButtonStyle() {
            if (!this.aiFeatureEnabled) return {}
            if (this.aiPanelCollapsed) {
                return { right: '8px', transform: 'none' }
            }
            // Main column ends at the panel seam; center the pill on that edge
            return { right: '0', transform: 'translateX(50%)' }
        },
    },

    beforeCreate() {
        this.localStore = new Vuex.Store({
            state: {
                aiModeEnabled: false,
                chatMessages: [],
                chatInput: '',
                isThinking: false,
                isStreaming: false,
                streamingMessageIndex: null,
                ss3: {
                    spriteSize: 64, // default size... changes depending on student count
                    studentSpritesOn: 3000, // under this we will ask for and render student image sprites
                    tiledSpritesOn: 8000, // above this we will use tiled sprites
                },
                highlightedFilter: null,
                openMoreFiltersToSection: null,
                params: {
                    search: {},
                    filters: [],
                },
                charts: {},
                selectedStudents: [],
                activeStudent: {
                    rec: null,
                    x: 0, y: 0,
                    show: false,
                    modal: false,
                },
                activeGrouping: {
                    type: null,
                    groups: [],
                },
                activeLayout: null,
                groupers: [
                    { name: 'School', type: 'property', args: { property: 'school_id', textProperty: 'school_name' }, id: Util.UUIDv4() },
                    { name: SCHOOL_GROUP_GROUPER_NAME, type: 'schoolGroup', args: { schoolGroups: [] }, id: Util.UUIDv4() },
                    { name: 'Grade', type: 'property', args: { property: 'grade_id', textProperty: 'grade_desc' }, id: Util.UUIDv4() },
                    {name:'Gender', type: 'property', args: {property: 'gender', textProperty: 'gender'}, id:Util.UUIDv4() },
                    {name:'Ethnicity', type: 'property', args: {property: 'ethnicity', textProperty: 'ethnicity'}, id:Util.UUIDv4() },
                    {
                        name:'Meal Status',
                        type: 'property',
                        security: ['SEARCH_MEAL_STATUS', 'DRILLDOWN_MEAL_STATUS'],
                        args: {property: 'meal_status_name', textProperty: 'meal_status_name'},
                        id:Util.UUIDv4()
                    },{
                        name:'English Learner',
                        el: true,
                        type: 'multiple',
                        subGroupers: [
                            {name:'EL Status', type: 'boolean', args: {property: 'ell_flag', textProperty: 'ell_flag'}, id:Util.UUIDv4() },
                            {name:'ELP Level', type: 'property', args: {property: 'ell_level_name', textProperty: 'ell_level_name'}, id:Util.UUIDv4() },
                        ],
                        id:Util.UUIDv4()
                    },
                    {
                        name:'Disability',
                        type: 'propertyArray',
                        args: {path: 'disabilities', property: 'description', emptyName: 'No Disability'},
                        infoText: "The percent calculated is the number of students with a disability in the category out of the total students filtered and may sum to greater than 100%.",
                        id:Util.UUIDv4()
                    },
                ],
                atlas: null,
                zoomed: true,
                vueLoaderId: null,
            },
            mutations: {},
            actions: {}
        })

        this.spriteSheets = {}
        this.oldSpriteSheets = {}
    },

    provide() {
        return {
            localStore: this.localStore,
            spriteSheets: this.spriteSheets,
            oldSpriteSheets: this.oldSpriteSheets,
        }
    },

    watch: {
        aiFeatureEnabled: {
            handler(val) {
                this.aiModeEnabled = val
                if (!val) {
                    this.aiPanelCollapsed = false
                }
            },
            immediate: true,
        },
    },

    mounted() {
        this.localStore.watch(state => state.params.search, () => {
            if (this.executeSearchInProgress) return
            this.executeSearch()
        })
        this.localStore.watch(state => state.params.filters, () => {
            if (this.executeSearchInProgress) return
            this.executeSearch()
        })
        this.vueLoaderId = this.$route.query.tstamp
        this.loadAccessibleSchoolGroups()
        this.$nextTick(this.applyGroupTabDataTests)
    },

    beforeUnmount() {
        // Clear filter data cache so fresh data is fetched when user returns
        // This ensures system config changes to display_name_group are reflected
        clearFilterCache()
    },

    updated() {
        this.applyGroupTabDataTests()
    },

    methods: {
        prefsFetched(v) {
            let prefs = {}
            v.forEach(pref => {
                prefs[pref.code] = pref.user_value !== "0"
            })
            this.$store.commit('set', {
                module: 'manage',
                state: 'districtPreferences',
                value: prefs
            })
        },

        applyGroupTabDataTests() {
            this.$nextTick(() => {
                const container = this.$refs.groupTabsRef?.$el
                if (!container) return
                const tabEls = container.querySelectorAll('.v-tab')
                this.groupTabsForFeTabs.forEach((tab, i) => {
                    if (tab.dataTest && tabEls[i]) tabEls[i].setAttribute('data-test', tab.dataTest)
                })
            })
        },

        // Handle coordinated search from child component
        handleCoordinatedSearch(searchParams) {
            // Prevent infinite loading state when user clicks search again with same values selected
            if (this.$_.isEqual(searchParams, this.params.search)) {
                return
            }
            // Start loading state immediately for visual feedback
            this.activeLayout = null
            this.$setLoading(true)
            // Note: Don't update params.search here to avoid triggering the watcher
            // The child will update parameters causing the watcher to trigger executeSearch when ready
        },
        
        onSearchClicked() {
            // Reset grouping to Overall when user explicitly clicks Search so tab highlight and chart stay in sync
            this.activeGrouping = { ...this.activeGrouping, grouper: null, type: null, groups: this.activeGrouping.groups || [] }
        },
        executeSearch() {
            // Prevent recursive calls while updating params.search
            if (this.executeSearchInProgress) {
                return
            }

            this.executeSearchInProgress = true

            // If search has never been run, params.search will be empty, backfill from search bar when a filter triggers search
            if (!this.params.search?.school_year_id) {
                const fromBar = this.$refs.demographicSearch?.getCurrentSearchParams?.()
                if (fromBar?.school_year_id) {
                    this.params.search = { ...fromBar, ...this.params.search }
                }
            }

            // Backfill missing school_year_id on year-scoped filters after params.search is
            // available, to ensure it is always present before fetching student data.
            const YEAR_SCOPED_FILTER_TYPES = ['assessment', 'attendance', 'incident', 'tag', 'disability']
            // Custom demographic filters are typed `demo_<id>` (one per demographic).
            const isDemographic = Util.isDemographicFilterType
            const isYearScoped = type => YEAR_SCOPED_FILTER_TYPES.includes(type) || isDemographic(type)
            if (this.params.search?.school_year_id) {
                // Same object the search bar's School Year field is already bound to.
                const schoolYear = this.$refs.demographicSearch?.schoolYear
                this.params.filters.forEach(filter => {
                    if (isYearScoped(filter.type) && filter.params && !filter.params.school_year_id) {
                        filter.params.school_year_id = this.params.search.school_year_id
                        // Keep the editor form's school_year in sync with the backfill above.
                        if (filter.editorParams && !filter.editorParams.school_year && schoolYear?.id === this.params.search.school_year_id) {
                            filter.editorParams.school_year = { ...schoolYear }
                        }
                        // Not a year anyone chose, so a demographic filter keeps following the search.
                        if (isDemographic(filter.type)) {
                            NlfAgentProcessor.markSchoolYearFollowsSearch(filter)
                        }
                    } else if (filter.schoolYearFollowsSearch && filter.params?.school_year_id !== this.params.search.school_year_id) {
                        // The search year changed since this filter's year was filled in from it.
                        const followed = schoolYear?.id === this.params.search.school_year_id ? { ...schoolYear } : null
                        filter.params.school_year_id = this.params.search.school_year_id
                        if (filter.editorParams) filter.editorParams.school_year = followed
                        if (filter.tooltipData) {
                            if (followed?.name) filter.tooltipData['School Year'] = followed.name
                            else delete filter.tooltipData['School Year']
                        }
                    }
                })
            }

            this.activeLayout = null
            this.$setLoading(true)

            this.$modelPost('students3d', 'read', Util.toApiParams(this.params)).then(response => {
                this.$setLoading(false)
                this.executeSearchInProgress = false

                if(this.$_.isEmpty(response.charts)) {
                    this.$errorPrompt({
                        title: 'Error',
                        message: response.msg || 'Unable to process server response.  Please contact support'
                    })
                    return
                }
                this.charts = Object.freeze(response.charts)

                if(this.activeGrouping.grouper) {
                    this.activeGrouping = Group.setGrouper(this.charts, this.activeGrouping.grouper)
                } else {
                    if(this.charts) {
                        this.activeGrouping = Group.none(this.charts)
                    }
                }

                this.$nextTick(() => {
                    const renderer = this.$refs.mainPanelRef?.$refs?.rendererRef
                    if (renderer && renderer.doLayout) renderer.doLayout(true, this.activeGrouping)
                })

                if(this?.charts?.records.length) {
                    this.ss3.spriteSize = 256
                    if(this.charts.records.length > 16) this.ss3.spriteSize = 128
                    if(this.charts.records.length > 512) this.ss3.spriteSize = 64
                    if(this.charts.records.length > 1600) this.ss3.spriteSize = 32

                    if(this.charts.records.length < this.ss3.studentSpritesOn) {
                        this.getSpriteSheets(this.charts.records)
                    }
                }
            }).catch((err) => {
                console.error('Error fetching students 3D data:', err)
                this.$setLoading(false)
                this.executeSearchInProgress = false
                this.$errorPrompt({
                    title: 'Error',
                    message: 'Unable to load results for this search. Please try again, or contact support if the problem continues.'
                })
            })
        },

        async loadAccessibleSchoolGroups() {
            let filteredGroups = []
            try {
                const schoolYearId = this.params?.search?.school_year_id
                const schoolUrl = this.$models.getUrl('school', 'read') + (schoolYearId ? `&school_year_id=${schoolYearId}` : '')
                const [schoolsRes, groupsRes] = await Promise.all([
                    this.$axios.get(schoolUrl),
                    this.$axios.get(this.$models.getUrl('schoolGroups', 'read'))
                ])
                const schoolsData = schoolsRes.data.schools || []
                const schoolGroupsData = groupsRes.data.school_group_v || []
                const accessibleSchoolIds = new Set(schoolsData.map(s => s.id))
                filteredGroups = schoolGroupsData.filter(group =>
                    group.schools && group.schools.some(schoolId => accessibleSchoolIds.has(schoolId))
                )
            } catch (err) {
                console.error('Error loading school groups for Students 3D:', err)
            }
            if (!Array.isArray(this.groupers) || !this.groupers.length) return
            this.groupers = this.groupers.map(g =>
                g.name === SCHOOL_GROUP_GROUPER_NAME ? { ...g, args: { schoolGroups: filteredGroups } } : g
            )
        },
        checkShow(c) {
            if (!c) return false
            const item = c.options || c
            if (!this.ellLevelEnabled && item.el) return false
            if (item.name === SCHOOL_GROUP_GROUPER_NAME) {
                return (item.args?.schoolGroups?.length ?? 0) > 0
            }
            if (!item.security) return true
            return this.$_.difference(item.security, this.sessionUser?.security_codes || []).length === 0
        },
        computeGrouping(grouper) {
            // When choosing a filter that creates a grouping:
            // show buckets now if we have student data, otherwise remember the choice until search finishes.
            const hasCharts = Array.isArray(this.charts?.records)
            return hasCharts
                ? Group.setGrouper(this.charts, grouper)
                : { ...this.activeGrouping, grouper }
        },
        applyMoreFilters(results) {
            // Types that live in the More Filters dialog; we replace all of them when Apply is clicked
            const moreTypes = ['course', 'disability', 'el_status', 'elp_level', 'ethnicity', 'meal_status', 'form']
            const filtersToRemove = this.params.filters.filter(f => moreTypes.includes(f.type))
            const groupers = Array.isArray(this.groupers) ? this.groupers : []

            // Drop groupers that belonged to any filter we're removing
            let newGroupers = groupers.filter(g => !filtersToRemove.some(f => f.grouper === g))
            const wasActiveRemoved = filtersToRemove.some(f => f.grouper && this.isActiveGrouper(f.grouper))

            // Remove all existing more-filter filters, then add the new set from the dialog
            this.params.filters = this.params.filters.filter(f => !moreTypes.includes(f.type))
            let addedGrouper = false
            const keys = ['course', 'disability', 'ell', 'ethnicity', 'meal_status', 'form']
            keys.forEach(key => {
                const value = results[key]
                if (value == null) return
                const filters = Array.isArray(value) ? value : [value]
                filters.forEach(f => {
                    this.params.filters.push(f)
                    if (f.grouper) {
                        newGroupers = [...newGroupers, f.grouper]
                        this.activeGrouping = this.computeGrouping(f.grouper)
                        addedGrouper = true
                    }
                })
            })
            this.groupers = newGroupers
            // If the user had a group-by active that we just removed, and no new one was added, clear grouping
            if (wasActiveRemoved && !addedGrouper) this.activeGrouping = Group.none(this.charts)
        },
        setFilter(newFilter, oldFilter) {
            const groupers = Array.isArray(this.groupers)
                ? this.groupers
                : (this.localStore?.state?.groupers || [])
            if (oldFilter) {
                if (newFilter === null || newFilter === undefined) {
                    this.params.filters = this.params.filters.filter(x => x !== oldFilter)
                    this.groupers = groupers.filter(x => x !== oldFilter.grouper)
                    if (this.isActiveGrouper(oldFilter.grouper)) this.activeGrouping = Group.none(this.charts)
                } else {
                    this.params.filters = this.params.filters.map(x => x === oldFilter ? newFilter : x)
                    this.groupers = groupers.map(x => x === oldFilter.grouper ? newFilter.grouper : x)
                    if (this.isActiveGrouper(oldFilter.grouper) && newFilter.grouper) {
                        this.activeGrouping = this.computeGrouping(newFilter.grouper)
                    } else if (this.isActiveGrouper(oldFilter.grouper)) {
                        this.activeGrouping.grouper = newFilter.grouper
                    }
                }
            } else {
                // Check for duplicate assessment filters before adding
                if (newFilter.type === 'assessment') {
                    const existingMatchingFilter = NlfAgentProcessor.findDuplicateAssessmentFilter(
                        this.params.filters,
                        newFilter.params
                    )

                    if (existingMatchingFilter) {
                        // Duplicate found - don't add it
                        return
                    }
                }

                if (newFilter.grouper) {
                    this.groupers = [...groupers, newFilter.grouper]
                    this.activeGrouping = this.computeGrouping(newFilter.grouper)
                }
                this.params.filters = [...this.params.filters, newFilter]
            }
        },
        isActiveGrouper(grouper) {
            if (!grouper) return false
            const id = this.activeGrouping.grouper?.id
            if (grouper.subGroupers) return grouper.subGroupers.some(x => x.id === id)
            return id === grouper.id
        },
        setGrouping(grouper) {
            if (this.isActiveGrouper(grouper)) return
            this.activeGrouping = Group.setGrouper(this.charts, grouper)
        },
        onGroupTabClick(tab) {
            if (!tab.grouper) {
                this.activeGrouping = Group.none(this.charts)
            } else {
                this.setGrouping(tab.grouper)
            }
        },
        handleSendMessage(content) {
            if (this.$refs.mainPanelRef) {
                this.$refs.mainPanelRef.sendMessageFromSidebar(content)
            }
        },
        handleSelectOption(option) {
            if (this.$refs.mainPanelRef) {
                this.$refs.mainPanelRef.selectOption(option)
            }
        },
        handleResetChat() {
            if (this.$refs.mainPanelRef) {
                this.$refs.mainPanelRef.resetChat()
            }
            if (this.$refs.demographicSearch) {
                this.$refs.demographicSearch.resetSearch()
            }
        },

        getSpriteSheets(studentRecs) {
            const ids = studentRecs.map(r => r.student_id)
            this.$axios.post(`ss3.php?action=generate&sprite_size=${this.ss3.spriteSize}&debug=${parseInt(this.debug)}`,
                {id: ids}
            ).then(response => {
                try {
                    if(response.data.index && response.data.index.length) {
                        let h = []
                        let atlas = []
                        const frameHeight = this.ss3.spriteSize
                        const frameWidth = this.ss3.spriteSize
                        response.data.index.forEach((v, i) => {
                            if (v.filename === 'defaultimg.png') return;

                            let h2 = []
                            v.data.forEach(d => {
                                h[d.id] = { filename: v.filename, x: d.x, y: d.y, default_pic: response.data.top_secret_mode }
                                if(!response.data.top_secret_mode) {
                                    h2[`${d.id}@${v.filename}`] = {
                                        frame: { x: d.x, y: d.y, w: frameWidth, h: frameHeight },
                                        sourceSize: { w: frameWidth, h: frameHeight }
                                    }
                                }
                            })

                            atlas[i] = {
                                meta: {
                                    image: `${ this.$axios.defaults.baseURL }ss3.php?action=get_image&file_name=${ v.filename }`,
                                    simpleName: v.filename
                                },
                                frames: h2
                            }
                        })

                        studentRecs.forEach(v => v.spriteSheet = h[v.student_id] )

                        this.atlas = Object.freeze(atlas)
                    } else {
                        throw new Error('No sprite sheet definitions returned.')
                    }
                } catch(error) {
                    console.warn('Warning: getSpriteSheets...', error)
                }
            })
        },
    }
}
</script>

<style lang="scss" scoped>
.students3d-demographic-wrap {
    flex: 0 0 auto;
    width: 100%;
}

.students3d-main-area {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: stretch;
}

.students3d-main-column {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    /* visible so the NLF toggle can sit half on the panel without clipping */
    overflow: visible;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
}

.students3d-pixi-row {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.nlf-agent-panel-toggle {
    position: absolute;
    z-index: 25;
    top: 8px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid #b3b7c5;
    background: #e9f9fe;
    color: #050f2d;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    line-height: 1;

    i {
        font-size: 16px;
    }

    &:hover {
        background: #d5ecf3;
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid #0679a0;
        outline-offset: 2px;
    }
}

.pixiwrapper {
    width: 100%;
    height: 100%;
}
.students3d-top-bar {
    background: #fff;
    padding-left: 16px;
    padding-right: 16px;
}
.top-bar-filters-row .filters-label {
    font-size: 14px;
    color: #050f2d;
}
/* Match fe-filter-btn look for modal trigger buttons (chronic-absence style); Figma border #7E8494 */
.top-bar-filters-row ::v-deep .filter-panel-btn .students3d-filter-trigger {
    background: #fff !important;
    border: 1px solid #7E8494 !important;
    font-size: 14px;
}
.top-bar-filters-row ::v-deep .filter-panel-btn .students3d-filter-trigger ::v-deep .v-btn__content {
    color: #050F2D;
}
.top-bar-filters-row ::v-deep .filter-panel-btn .students3d-filter-trigger:hover {
    background: var(--primary-color-light2, #e3f2fd) !important;
    border: 1px solid #7E8494 !important;
}
/* Active state (filter applied) - match fe-filter-btn-active */
.top-bar-filters-row ::v-deep .filter-panel-btn .students3d-filter-trigger.advanced-filter-btn-active {
    background: var(--primary-color-light2, #e3f2fd) !important;
    border: 1px solid var(--primary-color, #1976d2) !important;
}
.top-bar-filters-row ::v-deep .filter-panel-btn .students3d-filter-trigger.advanced-filter-btn-active ::v-deep .v-btn__content {
    color: var(--primary-color-dark, #1565c0) !important;
}
/* fe-filter-btn trigger (Gender, Ethnicity) - default border when not active */
.top-bar-filters-row ::v-deep .filter-panel-btn span.d-inline-block .v-btn:not(.fe-filter-btn-active) {
    border: 1px solid #7E8494 !important;
}
/* fe-filter-btn active state - same border as advanced-filter-btn (theme primary, e.g. #00798C) */
.top-bar-filters-row ::v-deep .filter-panel-btn span.d-inline-block .v-btn.fe-filter-btn-active {
    border: 1px solid var(--primary-color, #00798C) !important;
}

.students3d-group-tabs-row {
    align-items: stretch;
    flex-wrap: nowrap;
}
.students3d-group-tabs-row .students3d-group-tabs {
    min-width: 0;
    flex: 1 1 auto;
}
.students3d-group-tabs ::v-deep .v-tab {
    max-width: 200px;
    min-width: 0;
    overflow: hidden;
}
.students3d-group-tabs ::v-deep .v-tab a {
    display: block;
    min-width: 0;
    overflow: hidden;
}
.students3d-group-tabs ::v-deep .v-tab a span,
.students3d-group-tabs ::v-deep .v-tab .fe-tabs-dropdown-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.students3d-group-tabs {
    height: 56px !important;
    ::v-deep .fe-tabs-default {
        padding-left: 0 !important;
    }
    ::v-deep .fe-tabs-default .v-tabs__container {
        height: 56px;
    }
    ::v-deep .v-slide-group__prev--disabled,
    ::v-deep .v-slide-group__next--disabled {
        display: none !important;
    }
    ::v-deep .v-slide-group__content {
        min-width: var(--students3d-tab-bar-min-width, 0);
    }

    ::v-deep .fe-tabs-default .v-tab {
        font-size: 14px;
        min-width: 90px;
    }
}
</style>
